/**
 * The no-code form builder's storage.
 *
 * The point of versioning is that a submission is bound to the version of the form it was
 * completed against. A registration collected in July stays readable after the organiser adds a
 * field in August, and a consent record keeps the wording the person actually agreed to. Editing
 * a published form creates a new version rather than mutating the old one.
 */

import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { archivable, audited, flag, id, instant, json, ref, sortOrder } from "./columns.js";

export const FORM_PURPOSES = ["registration", "survey", "consent", "generic"] as const;

export const forms = sqliteTable(
  "forms",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id"),
    name: text("name").notNull(),
    purpose: text("purpose", { enum: FORM_PURPOSES }).notNull().default("registration"),
    isTemplate: flag("is_template").notNull().default(false),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("forms_community_idx").on(table.communityId, table.purpose)],
);

export const formVersions = sqliteTable(
  "form_versions",
  {
    id: id(),
    formId: ref("form_id").notNull(),
    communityId: ref("community_id").notNull(),
    version: integer("version").notNull(),
    publishedAt: instant("published_at"),
    /** The whole definition, denormalised. `form_fields` is the editable representation; this is
     *  the frozen one a submission is validated against, so a renderer never has to join. */
    schemaJson: json("schema_json"),
    ...audited(),
  },
  (table) => [uniqueIndex("form_versions_unique").on(table.formId, table.version)],
);

export const FIELD_TYPES = [
  "text",
  "long_text",
  "number",
  "date",
  "select",
  "multi_select",
  "checkbox",
  "phone",
  "email",
  "file",
  "consent",
  "heading",
] as const;

export const formFields = sqliteTable(
  "form_fields",
  {
    id: id(),
    formVersionId: ref("form_version_id").notNull(),
    communityId: ref("community_id").notNull(),
    key: text("key").notNull(),
    label: text("label").notNull(),
    helpText: text("help_text"),
    type: text("type", { enum: FIELD_TYPES }).notNull(),
    required: flag("required").notNull().default(false),
    optionsJson: json("options_json"),
    validationJson: json("validation_json"),
    /**
     * Conditional visibility. This is what makes a guardian block appear for a youth age band
     * without the youth question being hard-coded anywhere — the sample event's field list stays
     * one configuration among many.
     */
    visibleWhenJson: json("visible_when_json"),
    sortOrder: sortOrder(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("form_fields_key_unique").on(table.formVersionId, table.key),
    index("form_fields_version_idx").on(table.formVersionId, table.sortOrder),
  ],
);

export const formSubmissions = sqliteTable(
  "form_submissions",
  {
    id: id(),
    formVersionId: ref("form_version_id").notNull(),
    communityId: ref("community_id").notNull(),
    dataJson: json("data_json"),
    submittedAt: instant("submitted_at").notNull(),
    submittedBy: ref("submitted_by"),
    ...audited(),
  },
  (table) => [index("form_submissions_version_idx").on(table.formVersionId, table.submittedAt)],
);
